// The browser tab and the installed app wear the tenant's brand, applied at
// RUNTIME because index.html serves every tenant: its title, icons and manifest
// are the platform's (Porter) until the tenant is known, and never a tenant's.

const PLATFORM_MANIFEST = '/manifest.webmanifest'

// A slug is echoed into a URL path, so nothing else is let through.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/i

/** Mirrors brandManifestPath in supabase/functions/_shared/app-manifest.ts; a test holds them together. */
export function brandManifestHref(slug: string): string {
  return `/_brand/${slug}/manifest.webmanifest`
}

/** Set the tab / installed-app title. Falsy name leaves the default in place. */
export function applyDocumentTitle(name: string | null | undefined): void {
  if (!name) return
  document.title = name
  // iOS names a home-screen app from this, not from the manifest.
  document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute('content', name)
}

// The platform's own icon links, held while a tenant's logo replaces them.
let platformIconLinks: Element[] = []

/**
 * Point the tab icon at the tenant's logo.
 *
 * Existing icon links are REMOVED rather than left alongside a new one: with
 * several `rel="icon"` links present the choice between them is the browser's,
 * so leaving the platform icon in the document means some browsers keep showing
 * it. Ours is tagged so re-running replaces it instead of accumulating links.
 */
export function applyFavicon(logoUrl: string | null | undefined): void {
  if (!logoUrl) return // No logo configured — keep the platform icon.

  for (const link of Array.from(document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]'))) {
    if (!link.hasAttribute('data-brand-icon')) platformIconLinks.push(link)
    link.remove()
  }

  for (const rel of ['icon', 'apple-touch-icon']) {
    const link = document.createElement('link')
    link.rel = rel
    link.href = logoUrl
    link.setAttribute('data-brand-icon', '')
    document.head.appendChild(link)
  }
}

/** Put the platform's icons back in place of a previous tenant's logo. */
export function restorePlatformIcons(): void {
  const branded = document.querySelectorAll('link[data-brand-icon]')
  if (branded.length === 0) return
  branded.forEach((link) => link.remove())
  for (const link of platformIconLinks) document.head.appendChild(link)
  platformIconLinks = []
}

/** Point the install manifest at the tenant's own, or the platform's when no tenant is known. */
export function applyAppManifest(slug: string | null | undefined): void {
  const href = slug && SLUG_PATTERN.test(slug) ? brandManifestHref(slug) : PLATFORM_MANIFEST
  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'manifest'
    document.head.appendChild(link)
  }
  if (link.getAttribute('href') !== href) link.setAttribute('href', href)
}

/**
 * Brand the document for one tenant. A known tenant without a logo gets the
 * platform's icons back, never the last tenant's; `slug` moves the manifest
 * only when given, so a caller that does not know it leaves it alone.
 */
export function applyDocumentBranding(
  name: string | null | undefined,
  logoUrl: string | null | undefined,
  slug?: string | null,
): void {
  applyDocumentTitle(name)
  if (logoUrl) applyFavicon(logoUrl)
  else if (name) restorePlatformIcons()
  if (slug !== undefined) applyAppManifest(slug)
}
